import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { keywordHistory, checkKeyword } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Area,
  ComposedChart,
} from "recharts";
import { ArrowsClockwise, CaretLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function KeywordDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["history", id],
    queryFn: () => keywordHistory(id),
  });

  const checkMut = useMutation({
    mutationFn: () => checkKeyword(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["history", id] });
      qc.invalidateQueries({ queryKey: ["keywords"] });
      toast.success("Rank updated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Check failed"),
  });

  const keyword = data?.keyword;
  const snaps = data?.snapshots || [];

  // Coerce to chart-friendly shape. Treat "not in top 100" as 101 for chart, but display label as "—"
  const chartData = snaps.map((s) => ({
    date: new Date(s.checked_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    full_date: new Date(s.checked_at).toLocaleString(),
    position: s.position ?? 101,
    raw_position: s.position,
  }));

  const best = snaps.reduce(
    (acc, s) => (s.position != null && (acc == null || s.position < acc) ? s.position : acc),
    null,
  );
  const worst = snaps.reduce(
    (acc, s) => (s.position != null && (acc == null || s.position > acc) ? s.position : acc),
    null,
  );

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8">
      <Link
        to={keyword ? `/websites/${keyword.website_id}` : "/dashboard"}
        className="inline-flex items-center gap-1 text-xs label-eyebrow text-[var(--rt-text-muted)] hover:text-[var(--rt-text)] mb-4"
        data-testid="back-to-website"
      >
        <CaretLeft size={14} /> back to website
      </Link>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="label-eyebrow">/ keyword</div>
          <h1
            className="font-display text-5xl tracking-tight leading-none mt-2"
            data-testid="keyword-title"
          >
            {keyword?.keyword || "…"}
          </h1>
        </div>
        <Button
          data-testid="recheck-keyword-btn"
          onClick={() => checkMut.mutate()}
          disabled={checkMut.isPending}
          className="rounded-none bg-[var(--rt-primary)] hover:bg-[var(--rt-primary-hover)] text-white h-10 px-4 flex items-center gap-2"
        >
          <ArrowsClockwise size={14} className={checkMut.isPending ? "animate-spin" : ""} />
          {checkMut.isPending ? "Checking…" : "Check now"}
        </Button>
      </div>

      {/* KPI strip */}
      <div className="border border-[var(--rt-border)] bg-white p-6 grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
        <div>
          <div className="label-eyebrow">CURRENT</div>
          <div className="font-display text-4xl mt-1 leading-none">
            {keyword?.last_position ?? "—"}
          </div>
        </div>
        <div>
          <div className="label-eyebrow">PREVIOUS</div>
          <div className="font-display text-4xl mt-1 leading-none text-[var(--rt-text-muted)]">
            {keyword?.prev_position ?? "—"}
          </div>
        </div>
        <div>
          <div className="label-eyebrow">BEST EVER</div>
          <div className="font-display text-4xl mt-1 leading-none text-[var(--rt-success)]">
            {best ?? "—"}
          </div>
        </div>
        <div>
          <div className="label-eyebrow">CHECKS</div>
          <div className="font-display text-4xl mt-1 leading-none">{snaps.length}</div>
        </div>
      </div>

      {/* Chart */}
      <div className="border border-[var(--rt-border)] bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="label-eyebrow">/ rank history (lower is better)</div>
          <div className="text-xs text-[var(--rt-text-muted)] font-mono-data">
            BEST {best ?? "—"} · WORST {worst ?? "—"}
          </div>
        </div>
        {isLoading ? (
          <div className="h-72 animate-pulse bg-zinc-50" />
        ) : snaps.length === 0 ? (
          <div className="h-72 flex flex-col items-center justify-center text-center" data-testid="no-history">
            <div className="label-eyebrow">/ no data yet</div>
            <p className="text-sm text-[var(--rt-text-muted)] mt-2 max-w-sm">
              Click <span className="font-semibold">Check now</span> to fetch the current Google rank, or wait for the weekly run.
            </p>
          </div>
        ) : (
          <div className="h-80" data-testid="history-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="rankFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#002FA7" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#002FA7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E4E4E7" vertical={false} />
                <XAxis
                  dataKey="date"
                  stroke="#52525B"
                  tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }}
                  tickLine={false}
                />
                <YAxis
                  reversed
                  domain={[1, 101]}
                  ticks={[1, 10, 25, 50, 75, 100]}
                  stroke="#52525B"
                  tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }}
                  tickLine={false}
                  width={36}
                />
                <Tooltip
                  contentStyle={{
                    border: "1px solid #0A0A0A",
                    background: "#fff",
                    borderRadius: 0,
                    fontFamily: "IBM Plex Mono",
                    fontSize: 12,
                  }}
                  formatter={(value, _name, props) => [
                    props.payload?.raw_position ?? "Not in top 100",
                    "Position",
                  ]}
                  labelFormatter={(_, p) => p?.[0]?.payload?.full_date || ""}
                />
                <Area
                  type="monotone"
                  dataKey="position"
                  stroke="none"
                  fill="url(#rankFill)"
                />
                <Line
                  type="monotone"
                  dataKey="position"
                  stroke="#002FA7"
                  strokeWidth={2}
                  dot={{ r: 3, fill: "#002FA7" }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Recent checks table */}
      <div className="border border-[var(--rt-border)] bg-white mt-6">
        <div className="grid grid-cols-12 gap-4 px-4 py-3 label-eyebrow border-b border-[var(--rt-border)] bg-[#FAFAFA]">
          <div className="col-span-3">Date</div>
          <div className="col-span-1 text-right">Pos.</div>
          <div className="col-span-8 truncate">Matched URL</div>
        </div>
        {[...snaps].reverse().slice(0, 20).map((s) => (
          <div
            key={s.id}
            className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-[var(--rt-border)] last:border-b-0 rt-row-zebra items-center"
          >
            <div className="col-span-3 text-xs font-mono-data text-[var(--rt-text-muted)]">
              {new Date(s.checked_at).toLocaleString()}
            </div>
            <div className="col-span-1 text-right font-mono-data text-sm">
              {s.position ?? "—"}
            </div>
            <div className="col-span-8 truncate">
              {s.matched_url ? (
                <a
                  href={s.matched_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-[var(--rt-text-muted)] hover:text-[var(--rt-primary)] font-mono-data"
                >
                  {s.matched_url.replace(/^https?:\/\//, "")}
                </a>
              ) : (
                <span className="text-xs text-[var(--rt-text-muted)]">Not in top 100</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
